import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, of, filter, map, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BpService } from '../../../../core/services/bp.service';
import {
  Base64String,
  CertificateModalSignatureData,
  DocumentsToSign,
} from '../../../../shared/services/signature-nca-common-utils.service';
import { RegisterKkmSignatureForm } from '../register-kkm.model';
import { ReRegisterKkmSignatureForm } from '../../kkm-operations/re-register-kkm/re-register-kkm.model';
import { ConfirmBySmsComponent } from '../../../../shared/components/modals/confirm-by-sms/confirm-by-sms.component';
import { UsersService } from '../../../users/user.service';
import { ModalService } from '../../../../core/modal/modal.service';
import { TariffService } from '../../../../core/services/tariff.service';
import { Tariff } from '../../../tariffs/tariff.model';
import { openDocumentInTab } from '../../../../shared/file.utils';
import { KkmsApiService } from '../../../../core/modules/kkms/kkms-api.service';

@UntilDestroy()
@Component({
  selector: 'tax-kkm-sign',
  templateUrl: './kkm-sign.component.html',
  styleUrls: ['./kkm-sign.component.sass'],
})

export class KkmSignComponent implements OnInit, OnChanges {
  @Input() kkmForm?: RegisterKkmSignatureForm | ReRegisterKkmSignatureForm;
  @Output() goBack: EventEmitter<void> = new EventEmitter<void>();

  data?: CertificateModalSignatureData;
  documentLinkBilling?: SafeResourceUrl;
  selectedTariff?: Tariff;

  constructor(
    private router: Router,
    private kkmsApiService: KkmsApiService,
    private bpService: BpService,
    private modalService: ModalService,
    private sanitizer: DomSanitizer,
    private tariffService: TariffService,
    private usersService: UsersService,
  ) { }

  ngOnInit(): void {
    this.getDocumentsForSign();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('kkmForm' in changes) {
      this.getSelectedTariff();
    }
  }

  back(): void {
    this.goBack.emit();
  }

  openDocument(document: Base64String | undefined): void {
    if (!document) {
      return;
    }

    openDocumentInTab(document, {
      isBase64: true,
    });
  }

  setSignature(modalData: CertificateModalSignatureData): void {
    if (this.kkmForm?.formKey === 'kkm-inis-register-sing-kkm-annex') {
      // @ts-ignore
      this.kkmForm.fields.billingSignature = modalData.signature.billingSignature;
      // @ts-ignore
      this.kkmForm.fields.inisXmlSignature = modalData.signature.inisXmlSignature;
    } else {
      // @ts-ignore
      this.kkmForm.fields.signature = modalData.signature.signingDocument;
    }

    const { pid, tid } = this.bpService.getProcessIds();
    // @ts-ignore
    this.bpService.submit(pid, tid, this.kkmForm.fields)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.router.navigate(['main/kkms']);
        },
      });
  }

  signBySms(): void {
    this.usersService.getCurrentUserInfo()
      .pipe(
        switchMap(userInfo => this.modalService
          .openOld(ConfirmBySmsComponent, {
            title: 'sign.by.sms.title',
            buttonText: 'button.sign',
            businessProcessData: {
              IIN: userInfo.organisation.inn,
            },
            businessProcessMessage: 'inisSignBySmsMessage',
          })),
        filter(isSuccess => isSuccess),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          this.router.navigate(['main/kkms']);
        },
      });
  }

  private getDocumentsForSign(): void {
    const processPid = this.bpService.getProcessIds().pid;
    // @ts-ignore
    const isTariffSelected = Boolean(this.kkmForm.fields.tariffId) && this.kkmForm.fields.tariffId !== '0';
    combineLatest([
      this.kkmsApiService.fetchKkmRegistrationDocument(processPid),
      isTariffSelected ? this.kkmsApiService.fetchKkmChangeStatusDocument(processPid) : of(undefined),
    ])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ([inisDocument, billingDocument]) => {
          const documentsToSign: DocumentsToSign = this.kkmForm?.formKey === 'kkm-inis-register-sing-kkm-annex'
            ? {
              ...(billingDocument && {
                billingSignature: billingDocument,
              }),
              inisXmlSignature: inisDocument,
            }
            : {
              signingDocument: inisDocument,
            };

          this.data = {
            doc: documentsToSign,
            type: 'SIGN',
          };
          if (isTariffSelected) {
            const documentLinkBilling = this.kkmsApiService.fetchLinkToPdfKKkmChangeStatusDocument(processPid);
            this.documentLinkBilling = this.sanitizer.bypassSecurityTrustResourceUrl(`${documentLinkBilling}#toolbar=0&amp;navpanes=0`);
          }
        },
      });
  }

  private getSelectedTariff(): void {
    if ((this.kkmForm as RegisterKkmSignatureForm)?.fields.tariffId === '0') {
      return;
    }

    this.tariffService.getPublicTariffs()
      .pipe(
        map((tariffs: Tariff[]) => tariffs.find(tariff => tariff.id === Number((this.kkmForm as RegisterKkmSignatureForm)?.fields.tariffId))),
        untilDestroyed(this),
      )
      .subscribe((selectedTariff) => {
        this.selectedTariff = selectedTariff;
      });
  }
}
